import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import Logo from "@/components/Logo";
import { api, LOGO_URL } from "@/lib/api";

export default function ResetPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) toast.error("Missing or invalid reset token");
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) return toast.error("Password must be at least 6 characters");
    if (password !== confirm) return toast.error("Passwords don't match");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      toast.success("Password updated. Please sign in.");
      setTimeout(() => navigate("/login"), 1600);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reset failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid min-h-screen grid-cols-1 md:grid-cols-2">
      <div className="relative hidden flex-col justify-between bg-primary p-12 md:flex">
        <Link to="/"><Logo /></Link>
        <div>
          <img src={LOGO_URL} alt="" className="h-40 w-40 rounded-[22%] shadow-2xl" />
          <h2 className="mt-10 max-w-sm font-display text-4xl leading-tight tracking-tight">
            Pick a new
            <br />
            <span className="italic">password.</span>
          </h2>
          <p className="mt-4 max-w-sm text-sm text-foreground/70">
            Make it memorable. Make it yours.
          </p>
        </div>
        <div className="text-xs uppercase tracking-[0.22em] text-foreground/60">One‑time link</div>
      </div>

      <div className="flex flex-col justify-center bg-white px-6 py-12 md:px-16">
        <div className="mx-auto w-full max-w-sm">
          <div className="mb-10 md:hidden"><Logo /></div>

          {done ? (
            <div data-testid="reset-done">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary"><CheckCircle2 className="h-6 w-6" /></div>
              <p className="mt-6 text-xs font-medium uppercase tracking-[0.22em] text-muted-foreground">Done</p>
              <h1 className="mt-3 font-display text-4xl tracking-tight md:text-5xl">Password updated.</h1>
              <p className="mt-4 text-sm text-muted-foreground">Taking you to sign in...</p>
            </div>
          ) : (
            <>
              <p className="text-xs font-medium uppercase tracking-[0.22em] text-muted-foreground">Reset</p>
              <h1 className="mt-3 font-display text-4xl tracking-tight md:text-5xl">New password.</h1>

              <form onSubmit={submit} className="mt-10 space-y-6" data-testid="reset-form">
                <Field label="New password">
                  <input
                    type="password"
                    required
                    minLength={6}
                    data-testid="reset-password-input"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Minimum 6 characters"
                    className="w-full border-b-2 border-border bg-transparent py-3 text-lg outline-none focus:border-primary"
                  />
                </Field>
                <Field label="Confirm password">
                  <input
                    type="password"
                    required
                    minLength={6}
                    data-testid="reset-confirm-input"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    placeholder="Type it again"
                    className="w-full border-b-2 border-border bg-transparent py-3 text-lg outline-none focus:border-primary"
                  />
                </Field>
                <button
                  type="submit"
                  disabled={loading || !token}
                  data-testid="reset-submit-button"
                  className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-foreground px-6 py-4 text-sm font-medium text-background hover:bg-foreground/90 active:scale-95 disabled:opacity-60"
                >
                  {loading ? "Updating..." : "Update password"} <ArrowRight className="h-4 w-4" />
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="mb-2 text-[10px] font-medium uppercase tracking-[0.22em] text-muted-foreground">{label}</div>
      {children}
    </label>
  );
}
